import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { authService } from '../services';
import { ROUTES } from '../constants/route.constant';

const useStyles = makeStyles(() => ({
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1976d2 0%, #0d3a72 100%)',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    padding: 32,
    borderRadius: 12,
  },
  title: { fontWeight: 700, marginBottom: 4 },
  subtitle: { color: '#555', marginBottom: 24 },
  field: { marginBottom: 16 },
  button: { marginTop: 8, height: 44 },
  alert: { marginBottom: 16 },
}));

const LoginScreen: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (authService.isLoggedIn) history.replace(ROUTES.DASHBOARD);
  }, [history]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Please enter email and password.');
      return;
    }
    setLoading(true);
    const response = await authService.login({ email, password });
    setLoading(false);

    if (response?.error) {
      setError(response.message || 'Login failed.');
      return;
    }

    const accessToken = response?.data?.accessToken || response?.accessToken;
    if (!accessToken) {
      setError(response?.message || 'Login failed.');
      return;
    }
    const admin = response?.data?.admin || response?.admin || { email };
    authService.authSuccess({ accessToken, admin });
    history.replace(ROUTES.DASHBOARD);
  };

  return (
    <div className={classes.wrapper}>
      <Paper elevation={6} className={classes.card}>
        <Typography variant="h5" className={classes.title}>
          Xarothi Admin
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Sign in to manage products
        </Typography>
        {error && (
          <Alert severity="error" className={classes.alert}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            className={classes.field}
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
          <TextField
            className={classes.field}
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Button
            className={classes.button}
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
          </Button>
        </form>
      </Paper>
    </div>
  );
};

export default LoginScreen;
